/**
 * Schemas Zod do SGQ — Onda 2: NC, CAPA, reunião SACAP, Ishikawa, circular e
 * indicadores.
 *
 * O MESMO objeto valida no formulário React e no DTO do Nest (architecture.md
 * §9). No legado a validação era triplicada e divergente: o controller de NC
 * normalizava para `titulo`/`descricao`/`data_nc` e o model lia
 * `title_NC`/`evidencia`/`date_val` (BUG-NC-10) — se um template postasse os
 * nomes canônicos, a NC era gravada com título e descrição VAZIOS e a validação
 * passava.
 *
 * Referência: docs/legacy-spec/sgq.md, seções A a E.
 */
import { z } from 'zod';
import { AttendanceStatus, CapaActionStatus, CircularStatus, IshikawaCategory, NonconformitySource, NonconformityStatus, NonconformityType, } from './enums.js';
import { arrayQuerySchema, booleanQuerySchema, paginationSchema, signedCommandSchema, uuidSchema, uuidSchemaFor, } from './schemas.js';
/** Idêntico ao de `schemas.ts`: preserva o literal em vez de degradar a `string`. */
const enumValues = (e) => Object.values(e);
// ─────────────────────────────────────────────────────────────────────────────
// Catálogos administráveis — tipo de reunião e tipo de solicitação
//
// O legado tinha os dois (`erp_tipo_reuniao` e `erp_solicitacao.id_tipo`) e a
// Onda 2 os substituiu por enum fechado, para matar a detecção de SACAP por
// `LIKE '%sacap%'` sobre o nome. Aqui o catálogo volta administrável e o nome
// deixa de significar qualquer coisa para o sistema: quem carrega a regra são
// as flags. Renomear um tipo passa a ser operação de vocabulário, e nada mais.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Código do tipo. Maiúsculas, sem espaço, e IMUTÁVEL depois de criado — ele
 * viaja para `meeting.kind` e entra no payload assinado da ata.
 */
const typeCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .min(2, 'O código precisa de ao menos dois caracteres.')
    .max(32)
    .regex(/^[A-Z][A-Z0-9_]{1,31}$/, 'Use letras maiúsculas, números e sublinhado, começando por letra. Exemplo: ANALISE_CRITICA.');
const supportTypeBaseSchema = z.object({
    name: z.string().trim().min(1, 'Informe o nome do tipo.').max(255),
    description: z.string().trim().max(4000).default(''),
    sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
    active: z.boolean().default(true),
});
export const createMeetingTypeSchema = supportTypeBaseSchema.extend({
    code: typeCodeSchema,
    /** Exige ao menos uma não-conformidade vinculada para agendar. */
    requiresNonconformity: z.boolean().default(false),
    /** Exige avaliação de eficácia depois do encerramento — ISO 13485 §8.5.2. */
    requiresEffectiveness: z.boolean().default(false),
});
/** O código sai do conjunto editável: é a chave usada em registro já assinado. */
export const updateMeetingTypeSchema = createMeetingTypeSchema.omit({ code: true }).partial();
export const createRequestTypeSchema = supportTypeBaseSchema.extend({
    code: typeCodeSchema,
    /** Exige causa-raiz — e, por consequência, caso CAPA e não-conformidade. */
    requiresRootCause: z.boolean().default(false),
    /** Exige "por quê" e "como" preenchidos. Desligado, a solicitação é enxuta. */
    requiresFullPlan: z.boolean().default(true),
    /** Marca a solicitação como preventiva por padrão — ISO 13485 §8.5.3. */
    preventiveDefault: z.boolean().default(false),
});
export const updateRequestTypeSchema = createRequestTypeSchema.omit({ code: true }).partial();
/**
 * Filtro dos catálogos.
 *
 * `includeInactive` existe para que a TELA DE ADMINISTRAÇÃO enxergue o que
 * desativou — sem ele, desativar um tipo o faria sumir da própria tela onde se
 * desativa, e não haveria como reativá-lo. Os formulários de reunião e de
 * solicitação chamam sem a flag e recebem só os ativos.
 */
export const supportTypeListFilterSchema = z.object({
    includeInactive: booleanQuerySchema.default(false),
    search: z.string().trim().max(255).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Matriz GUT — sgq.md §A.2
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Os três fatores, 1..5.
 *
 * PARIDADE sgq.md §A.2.1: o legado aceitava os aliases `gravidade|Gravidade`,
 * `urgencia|urgente|Urgencia|Urgente` e `tendencia|Tendencia` no controller
 * (`controller/qualidade/qualidade.php:106-108`) e o model lia OUTRO conjunto —
 * daí o BUG-NC-10. Aqui há UM nome por fator, em inglês, e o rótulo pt-BR mora
 * na tela.
 */
export const gutScoreSchema = z.object({
    severity: z.coerce.number().int().min(1, 'Avaliação de gravidade é obrigatória.').max(5),
    urgency: z.coerce.number().int().min(1, 'Avaliação de urgência é obrigatória.').max(5),
    trend: z.coerce.number().int().min(1, 'Avaliação de tendência é obrigatória.').max(5),
});
// ─────────────────────────────────────────────────────────────────────────────
// Não-conformidade — sgq.md §A
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Abertura de NC.
 *
 * `evidence` é o `erp_nc.descricao` do legado, cujo `name` no formulário era
 * literalmente `evidencia` e cujo rótulo era "Evidência" (sgq.md §A.6). O nome
 * do campo passa a dizer o que ele é.
 *
 * `recurrenceOfIds` reproduz o CSV de ids de `erp_nc_reincidencia` (§A.4):
 * uma NC reincidente pode apontar para N NCs de origem.
 */
export const createNonconformitySchema = z.object({
    title: z.string().trim().min(5, 'Informe um título com ao menos 5 caracteres.').max(255),
    /** Legado: `erp_nc.descricao`, o campo "Evidência" do formulário. */
    evidence: z.string().trim().min(1, 'Descreva a evidência da não-conformidade.').max(20_000),
    type: z.enum(enumValues(NonconformityType)).default(NonconformityType.NONCONFORMITY),
    /** Campo NOVO: no legado a origem era inferida do tipo (sgq.md §A.7). */
    source: z.enum(enumValues(NonconformitySource)),
    /** Setor onde a NC foi detectada. Legado: `erp_nc.departamento`. */
    departmentId: uuidSchema,
    /** Responsável pelo tratamento. Legado: `erp_nc.funcionario`. */
    assigneeUserId: uuidSchema,
    /** Data do fato. `DATE` puro — o legado guardava `VARCHAR dd/mm/aaaa` (C8). */
    detectedOn: z.coerce.date({ errorMap: () => ({ message: 'Data inválida.' }) }),
    /** GUT do EMISSOR. Legado: `erp_nc.criticidade`. */
    gut: gutScoreSchema,
    /** Evidências em MEDIA. O legado guardava metadados do Google Drive (§A.6). */
    evidenceUploadIds: z.array(uuidSchema).max(20).default([]),
    /** NCs de origem, quando reincidente. Legado: `erp_nc_reincidencia`. */
    recurrenceOfIds: z.array(uuidSchema).max(50).default([]),
});
/**
 * Edição do registro ANTES da avaliação do gestor.
 *
 * Não é ato assinado: enquanto a NC está `OPEN`, corrigir um erro de digitação
 * é higiene, não mudança de status regulatório (plano §5.3). Depois de avaliada,
 * nenhum destes campos muda — a máquina não tem aresta de volta para `OPEN`,
 * exatamente como o legado, que também não tinha reabertura (§A.1).
 */
export const updateNonconformitySchema = createNonconformitySchema
    .partial()
    .omit({ recurrenceOfIds: true, evidenceUploadIds: true })
    .extend({
    changeReason: z.string().trim().min(1, 'Informe o motivo da correção.').max(2000),
});
/**
 * Filtros da grade.
 *
 * PARIDADE sgq.md §A.8: `filtro_env_rec` do legado (`'R'` = recebidas,
 * qualquer outra coisa = emitidas) vira `scope`, com um terceiro valor `ALL`
 * que o legado não tinha. O que NÃO é preservado é o `JOIN erp_departamento_gerente`
 * sem `DISTINCT`, que duplicava cada NC por gerente do setor e FAZIA SUMIR a NC
 * de setor sem gerente cadastrado (BUG-NC-09).
 */
export const nonconformityListFilterSchema = paginationSchema.extend({
    /** `ISSUED` = emitidas por mim; `RECEIVED` = sob minha responsabilidade. */
    scope: z.enum(['ISSUED', 'RECEIVED', 'ALL']).default('ALL'),
    status: arrayQuerySchema(z.enum(enumValues(NonconformityStatus))).optional(),
    type: arrayQuerySchema(z.enum(enumValues(NonconformityType))).optional(),
    source: arrayQuerySchema(z.enum(enumValues(NonconformitySource))).optional(),
    departmentId: uuidSchema.optional(),
    assigneeUserId: uuidSchema.optional(),
    /** Busca por número sequencial ou trecho do título. */
    search: z.string().trim().max(255).optional(),
    detectedFrom: z.coerce.date().optional(),
    detectedTo: z.coerce.date().optional(),
    onlyRecurrent: booleanQuerySchema.default(false),
    onlyOverdueCapa: booleanQuerySchema.default(false),
    sortBy: z.enum(['number', 'detectedOn', 'criticality', 'status']).default('number'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
/**
 * Avaliação do gestor — ATO ASSINADO (`NC_EVALUATE` ou `NC_NOT_APPLICABLE`).
 *
 * PARIDADE sgq.md §A.3: são os dois passos encadeados do mesmo botão do legado
 * (procedência + reavaliação GUT), unificados num comando só porque são um ato
 * só do ponto de vista regulatório.
 *
 * DIVERGÊNCIA: o legado forçava `procede = 'S'` INCONDICIONALMENTE em
 * `managerEvaluation` e gravava `status_nc = 'F'` no MESMO UPDATE
 * (`model/qualidade/qualidade.php:32-37`) — a NC nascia e morria na avaliação,
 * sem nenhum plano de ação. Aqui `proceeds: true` leva a `ACCEPTED`, que é o
 * começo do tratamento, não o fim.
 */
export const evaluateNonconformitySchema = signedCommandSchema
    .extend({
    proceeds: z.boolean(),
    /** GUT do GESTOR. Legado: `erp_nc.manager_rate`, coluna separada. */
    gut: gutScoreSchema.optional(),
    /** Legado: `erp_nc.justificativa` (procede) / `justificativa_nao_procede`. */
    justification: z.string().trim().min(1, 'Informe a justificativa da avaliação.').max(4000),
})
    .superRefine((data, ctx) => {
    // Procedente EXIGE reavaliação GUT: é o dado que prioriza a fila de CAPA.
    // Sem ele, "procede" não diz o quanto importa.
    if (data.proceeds && !data.gut) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['gut'],
            message: 'Refaça a matriz GUT ao considerar a não-conformidade procedente.',
        });
    }
});
/**
 * Encerramento — ATO ASSINADO (`NC_CLOSE`).
 *
 * O comando não tem campo que possa "forçar" o encerramento. A completude do
 * CAPA é invariante da transição (`assertCapaIsComplete` em @qualicloud/domain),
 * não parâmetro do chamador — que é a diferença exata para o legado, em que a
 * regra existia numa função sem chamador (BUG-NC-04).
 */
export const closeNonconformitySchema = signedCommandSchema.extend({
    /** Conclusão do tratamento, para a ata e o relatório consolidado. */
    closingStatement: z
        .string()
        .trim()
        .min(1, 'Registre a conclusão do tratamento da não-conformidade.')
        .max(4000),
});
/**
 * Vínculo de reincidência — sgq.md §A.4.
 *
 * PARIDADE INTEGRAL: o vínculo é puramente informativo/rastreável. Não altera
 * criticidade, não muda status, não força SACAP e não notifica. O legado era
 * assim e está certo: transformar reincidência em gatilho automático embutiria
 * política de SGQ no produto.
 *
 * O que muda: a reincidência passa a ALIMENTAR O INDICADOR de taxa de
 * reincidência (§E.6 lista isso entre os indicadores que NÃO existiam).
 */
export const ncRecurrenceSchema = z.object({
    /** NCs de origem — as que já haviam registrado o mesmo problema. */
    originNonconformityIds: z
        .array(uuidSchema)
        .min(1, 'Informe ao menos uma não-conformidade de origem.')
        .max(50),
    note: z.string().trim().max(2000).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Reunião — sgq.md §B
// ─────────────────────────────────────────────────────────────────────────────
const meetingBaseSchema = z.object({
    subject: z.string().trim().min(1, 'Informe o assunto da reunião.').max(255),
    /**
     * O tipo, escolhido no catálogo da organização (`meeting_type`).
     *
     * Era `kind: z.enum([...])` — três valores fixos no código. O que decidia se a
     * reunião exigia não-conformidade e avaliação de eficácia era o literal
     * `'SACAP'`. Agora quem decide são as flags da linha do tipo, e o contrato não
     * tem como conhecê-las: quem valida a coerência é o serviço, depois de
     * carregar o tipo. Por isso o `superRefine` de NC saiu daqui.
     */
    meetingTypeId: uuidSchema,
    location: z.string().trim().min(1, 'Informe o local da reunião.').max(255),
    /** `TIMESTAMPTZ`. O legado convertia `dd/mm/aaaa hh:mm` por `substr` (BUG-REU-13). */
    startsAt: z.coerce.date({ errorMap: () => ({ message: 'Data de início inválida.' }) }),
    endsAt: z.coerce.date({ errorMap: () => ({ message: 'Data de término inválida.' }) }),
    /** Legado: `erp_reuniao.pauta`, campo de POST chamado `message`. */
    agenda: z.string().trim().min(1, 'Informe a pauta da reunião.').max(65_535),
    participantUserIds: z
        .array(uuidSchema)
        .min(1, 'A reunião precisa de ao menos um participante.')
        .max(200),
});
export const createMeetingSchema = meetingBaseSchema
    .extend({
    /**
     * NCs tratadas.
     *
     * Deixou de ser obrigatório aqui. A exigência não é do formulário nem de um
     * tipo específico: é da flag `requiresNonconformity` do tipo escolhido, que
     * só o servidor conhece. Quem recusa é `assertMeetingTypeCoherence`, no
     * domínio, com a mensagem que nomeia o tipo — e não mais "uma SACAP precisa".
     */
    nonconformityIds: z.array(uuidSchema).max(100).default([]),
})
    .superRefine((data, ctx) => {
    if (data.endsAt <= data.startsAt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endsAt'],
            message: 'O término precisa ser posterior ao início.',
        });
    }
});
/**
 * Edição da reunião.
 *
 * ⚠️ A GUARDA ANTI-WIPE MORA AQUI, na forma do tipo.
 *
 * PARIDADE INVERTIDA sgq.md §B.5 / BUG-REU-07: no legado `updateReuniao` sem
 * `nc[]` no POST chamava `syncNcToReuniao(id, array())` e **apagava todos os
 * vínculos de NC**, silenciosamente. Como `save()` e `finish()` mandavam o POST
 * completo, qualquer formulário que não reenviasse o campo desvinculava a NC da
 * SACAP e matava a rastreabilidade NC↔reunião.
 *
 * `nonconformityIds` e `participantUserIds` são OPCIONAIS e não têm default:
 * ausente significa "não mexi nisso", nunca "esvazie". É a mesma classe do bug
 * de distribuição do GED (ged.md §5.7 item 2), e a mesma solução.
 */
export const updateMeetingSchema = meetingBaseSchema
    .partial()
    .omit({ participantUserIds: true })
    .extend({
    participantUserIds: z.array(uuidSchema).max(200).optional(),
    nonconformityIds: z.array(uuidSchema).max(100).optional(),
})
    .superRefine((data, ctx) => {
    if (data.startsAt && data.endsAt && data.endsAt <= data.startsAt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endsAt'],
            message: 'O término precisa ser posterior ao início.',
        });
    }
});
/**
 * Filtros da grade de reunião — sgq.md §B.3.
 *
 * O legado excluía canceladas por padrão (`if(!$canceladas) ... dt_cancela is null`,
 * `model/reuniao/reuniao.php:495`); preservado em `includeCancelled`.
 */
export const meetingListFilterSchema = paginationSchema.extend({
    status: arrayQuerySchema(z.enum([
        'SCHEDULED',
        'IN_PROGRESS',
        'AWAITING_CLOSURE',
        'CLOSED',
        'AWAITING_EFFECTIVENESS',
        'CANCELLED',
    ])).optional(),
    meetingTypeIds: arrayQuerySchema(uuidSchema).optional(),
    /** `ORGANIZER` = emiti; `PARTICIPANT` = fui convidado. Legado: `env_rec`. */
    scope: z.enum(['ORGANIZER', 'PARTICIPANT', 'ALL']).default('ALL'),
    nonconformityId: uuidSchema.optional(),
    search: z.string().trim().max(255).optional(),
    startsFrom: z.coerce.date().optional(),
    startsTo: z.coerce.date().optional(),
    includeCancelled: booleanQuerySchema.default(false),
    sortBy: z.enum(['startsAt', 'subject', 'status']).default('startsAt'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
/**
 * Confirmação de presença — sgq.md §B.3.
 *
 * O legado só aceitava `'1'` e `'2'` e devolvia `success` apenas para `'1'`
 * (`controller/reuniao/reuniao.php:493-509`): recusar um convite parecia falha.
 * `ATTENDED`/`ABSENT` são registrados por quem conduz, na abertura.
 */
export const meetingAttendanceSchema = z.object({
    status: z.enum(enumValues(AttendanceStatus)),
    /** Alvo. Ausente = o próprio usuário autenticado (auto-confirmação). */
    userId: uuidSchema.optional(),
    note: z.string().trim().max(1000).optional(),
});
/**
 * Encerramento com ata — ATO ASSINADO (`MEETING_CLOSE`).
 *
 * PARIDADE INVERTIDA sgq.md §B.3: o `finish()` do legado **não validava nada** —
 * não checava pauta, participantes, tarefas em aberto, permissão, nem se a
 * reunião já estava encerrada, e o `UPDATE dt_encerramento = NOW()` era
 * idempotente-destrutivo. Aqui o encerramento é assinado e passa por
 * `assertMeetingCanBeClosed`.
 */
export const closeMeetingSchema = signedCommandSchema.extend({
    /** Ata. Texto congelado no ato — `meeting_minutes` é append-only. */
    minutes: z.string().trim().min(1, 'A ata é obrigatória para encerrar a reunião.').max(65_535),
    /** Presença efetiva por participante, apurada na reunião. */
    attendance: z
        .array(z.object({
        userId: uuidSchema,
        status: z.enum([AttendanceStatus.ATTENDED, AttendanceStatus.ABSENT]),
    }))
        .default([]),
});
/**
 * Cancelamento da reunião — sgq.md §B.1 métodos 2 e 3 (`justifica()` e
 * `cancelar()`, `controller/reuniao/reuniao.php:14` e `:25`).
 *
 * O legado tinha um fragmento de tela só para pedir a justificativa
 * (`justifica()`) e um endpoint que gravava `dt_cancela`, `id_usuario_cancela` e
 * `justificativa` (`model/reuniao/reuniao.php:173-183`). No produto novo o
 * estado `CANCELLED`, as colunas, o CHECK `meeting_cancelled_has_reason` e as
 * arestas da máquina já existiam — faltava o caminho. Reunião agendada por
 * engano ficava no sistema para sempre, poluindo a grade e o indicador.
 *
 * A justificativa é OBRIGATÓRIA, como no legado: cancelar sem dizer por quê
 * apaga a análise crítica sem deixar registro do motivo (ISO 13485 §5.6).
 */
export const cancelMeetingSchema = z.object({
    justification: z
        .string()
        .trim()
        .min(10, 'Descreva o motivo do cancelamento em pelo menos 10 caracteres.')
        .max(4000),
});
/**
 * Eficácia da SACAP — sgq.md §B.7.
 *
 * O legado exigia justificativa apenas no JavaScript, e só no caminho "eficaz"
 * (`reuniao_list.twig:292-297`); o servidor não exigia em nenhum dos dois
 * (`controller/reuniao/reuniao.php:1129-1154`). Aqui é obrigatória sempre: é o
 * registro de que a análise crítica aconteceu (ISO 13485 §8.5.2).
 */
export const meetingEffectivenessSchema = z.object({
    effective: z.boolean(),
    justification: z
        .string()
        .trim()
        .min(1, 'Informe a justificativa da avaliação de eficácia da SACAP.')
        .max(4000),
});
// ─────────────────────────────────────────────────────────────────────────────
// Ishikawa e causa-raiz — sgq.md §B.6
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Uma causa no espinha-de-peixe.
 *
 * `priority` é o `sort_order` do legado que NUNCA FUNCIONOU: era gravado
 * literal `0` (`model/reuniao/reuniao.php:560`) e não havia endpoint de
 * reordenação, então o `ORDER BY tipo, sort_order` degenerava em ordem
 * alfabética de categoria (BUG-REU-09). Aqui é campo de entrada, obrigatório e
 * com faixa validada — arrastar a causa passa a ter efeito.
 *
 * As 6 categorias são os 6M clássicos de `IshikawaCategory`. O legado usava
 * `('Sistemas','Pessoas','Equipamentos','Materiais','Métodos','Ambiente')` como
 * STRING LIVRE de 64 caracteres, sem tabela nem validação — dois analistas
 * digitando "Metodo" e "Método" produziam duas categorias.
 */
export const createRootCauseSchema = z.object({
    category: z.enum(enumValues(IshikawaCategory)),
    description: z.string().trim().min(1, 'Descreva a causa.').max(512),
    /** 1 = mais prioritária. Ordena o plano de ação. */
    priority: z.coerce.number().int().min(1).max(999).default(1),
    /** Marca a causa como RAIZ confirmada (após os 5 porquês). */
    isRootCause: z.boolean().default(false),
});
export const updateRootCauseSchema = createRootCauseSchema.partial();
// ─────────────────────────────────────────────────────────────────────────────
// Plano de ação 5W2H — sgq.md §C
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Ação corretiva 5W2H.
 *
 * PARIDADE RESTAURADA sgq.md §C.1 / BUG-TAR-01: os sete campos `w_*` existiam na
 * tabela e eram **inalcançáveis pela interface**. Só `reuniao/tarefas/addTask`
 * os gravava — num `UPDATE` separado, dentro de um `try/catch` que só logava — e
 * NENHUM template do sistema chamava aquela rota. Toda tarefa real nascia por
 * `reuniao/reuniao/addSS`, cujo INSERT não contemplava o 5W2H. Resultado: as
 * colunas ficavam sempre NULL e a seção "Plano de ação 5W2H" do PDF da NC saía
 * vazia. O requisito C10 estava implementado no backend e ausente do produto.
 *
 * Aqui os campos são obrigatórios na criação (exceto `howMuch`, opcional por
 * decisão de domínio — ver `@qualicloud/domain/capa`), e `when`/`who` são
 * colunas tipadas, não texto livre: no legado `w_when` era `VARCHAR(50)` e
 * `w_who` `VARCHAR(255)`, ou seja, o prazo não era comparável e o responsável
 * não era um usuário.
 */
export const createCapaActionSchema = z.object({
    title: z.string().trim().min(1, 'Informe o título da solicitação.').max(255),
    /**
     * Tipo da solicitação. Restaura o `tipoSS` do legado, que era obrigatório na
     * criação (sgq.md §C.2) e não sobreviveu à Onda 2.
     *
     * É ele que diz se a solicitação precisa de causa-raiz e se o plano 5W2H é
     * cobrado por inteiro.
     */
    requestTypeId: uuidSchema,
    /**
     * Causa-raiz que a solicitação ataca. OPCIONAL no contrato: a solicitação
     * avulsa não tem causa, como `erp_solicitacao.id_ishikawa_causa` NULL do
     * legado (sgq.md §775). Quem exige é `requestType.requiresRootCause`, e a
     * recusa vem do servidor com `CAPA.NO_ROOT_CAUSE`.
     */
    rootCauseId: uuidSchema.nullish(),
    /** What — legado `w_what`. */
    what: z.string().trim().min(1, 'Informe o que será feito.').max(20_000),
    /**
     * Why — legado `w_why`.
     *
     * Vazio é aceito no contrato e recusado pelo servidor quando o tipo exige o
     * plano completo. Cobrar "por quê" de um pedido de troca de lâmpada produz
     * campo preenchido com ponto final, que é pior do que campo ausente: parece
     * justificativa e não é.
     */
    why: z.string().trim().max(20_000).default(''),
    /** Where — legado `w_where`. */
    where: z.string().trim().min(1, 'Informe onde a solicitação será executada.').max(255),
    /** When — legado `w_when VARCHAR(50)`. Aqui é `DATE`. */
    dueDate: z.coerce.date({ errorMap: () => ({ message: 'Prazo inválido.' }) }),
    /**
     * Who — legado `w_who VARCHAR(255)`. Aqui é o `userId` do responsável.
     *
     * A mensagem é a do CAMPO, não a do tipo. "Identificador inválido" é o
     * sistema falando de si mesmo: quem deixou o seletor vazio precisa ler
     * "escolha o responsável", que é o que ele tem a fazer.
     */
    assigneeUserId: uuidSchemaFor('Escolha o responsável pela execução.'),
    /** How — legado `w_how`. Mesma regra do "por quê": exigido pelo TIPO. */
    how: z.string().trim().max(20_000).default(''),
    /** How much — legado `w_howmuch VARCHAR(50)`. Opcional; nem toda ação tem custo. */
    howMuch: z.coerce.number().min(0).max(1_000_000_000).nullable().default(null),
    departmentId: uuidSchemaFor('Escolha o departamento.'),
    /** Ação preventiva x corretiva. ISO 13485 §8.5.2 vs §8.5.3. */
    preventive: z.boolean().default(false),
});
export const updateCapaActionSchema = createCapaActionSchema
    .partial()
    // A origem e o tipo não se editam: mudar o tipo de uma solicitação em curso
    // mudaria as regras a que ela já se submeteu, e mudar a causa reescreveria a
    // que motivou o plano. Ambos são ato de criar outra solicitação.
    .omit({ assigneeUserId: true, requestTypeId: true, rootCauseId: true })
    .extend({
    changeReason: z.string().trim().min(1, 'Informe o motivo da alteração.').max(2000),
});
/**
 * Filtros da grade de ações — sgq.md §C.4.
 *
 * Precedência de escopo do legado preservada: setor gerenciado > enviadas por
 * mim > atribuídas a mim (`model/reuniao/tarefas.php:528-543`).
 */
export const capaActionListFilterSchema = paginationSchema.extend({
    scope: z.enum(['ASSIGNED_TO_ME', 'REQUESTED_BY_ME', 'DEPARTMENT', 'ALL']).default('ALL'),
    status: arrayQuerySchema(z.enum(enumValues(CapaActionStatus))).optional(),
    nonconformityId: uuidSchema.optional(),
    capaCaseId: uuidSchema.optional(),
    meetingId: uuidSchema.optional(),
    requestTypeIds: arrayQuerySchema(uuidSchema).optional(),
    /** Só as que não vieram de tratamento de não-conformidade. */
    onlyStandalone: booleanQuerySchema.default(false),
    departmentId: uuidSchema.optional(),
    assigneeUserId: uuidSchema.optional(),
    dueFrom: z.coerce.date().optional(),
    dueTo: z.coerce.date().optional(),
    onlyOverdue: booleanQuerySchema.default(false),
    onlyAwaitingMyVerification: booleanQuerySchema.default(false),
    search: z.string().trim().max(255).optional(),
    sortBy: z.enum(['dueDate', 'status', 'title', 'createdAt']).default('dueDate'),
    sortDir: z.enum(['asc', 'desc']).default('asc'),
});
/**
 * Registro de execução — o "diário de bordo" `erp_solicitacao_exec`.
 *
 * `transition` substitui os endpoints `startTaskAction`/`finishTaskAction` do
 * legado, que sofriam do BUG-TAR-07: quando o `WHERE ... AND id_responsavel = ?`
 * não casava, o `UPDATE` afetava zero linhas e o controller respondia
 * `{'FEEDBACK':'OK'}` do mesmo jeito. O usuário via sucesso e nada acontecia.
 */
export const recordActionExecutionSchema = z.object({
    /** Texto da execução. Legado: `erp_solicitacao_exec.execucao`. */
    note: z.string().trim().min(1, 'Descreva o que foi executado.').max(20_000),
    /** Movimento de estado que este registro provoca. `null` = só anota. */
    transition: z.enum(['START', 'COMPLETE']).nullable().default(null),
    /** Percentual concluído, para o acompanhamento. */
    progressPercent: z.coerce.number().int().min(0).max(100).optional(),
    evidenceUploadIds: z.array(uuidSchema).max(20).default([]),
});
/**
 * Verificação de eficácia — ATO ASSINADO
 * (`EFFECTIVENESS_VERIFY` ou `EFFECTIVENESS_REJECT`).
 *
 * PARIDADE sgq.md §C.5: quem verifica é o SOLICITANTE da ação, e a justificativa
 * é obrigatória quando reprovada. As duas regras vivem em
 * `assertCanVerifyEffectiveness` (@qualicloud/domain), não aqui — o schema é a
 * primeira barreira, não a regra.
 */
export const verifyEffectivenessSchema = signedCommandSchema
    .extend({
    approved: z.boolean(),
    /** Legado: prefixo `[Justificativa - Reabertura]` na execução. */
    justification: z.string().trim().max(4000).nullish(),
    /** Como a eficácia foi comprovada. ISO 13485 §8.5.2. */
    evidenceSummary: z.string().trim().max(4000).nullish(),
})
    .superRefine((data, ctx) => {
    if (!data.approved && !(data.justification ?? '').trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['justification'],
            message: 'Informe a justificativa quando a ação não resolveu o problema.',
        });
    }
});
/**
 * Reatribuição — sgq.md §C.4.
 *
 * As nove guardas do `reassignTask` do legado são preservadas no serviço
 * (ação não finalizada, não cancelada, ator gerente do setor, destino ativo e do
 * mesmo setor, idempotência). O que muda é que a troca vira linha no diário de
 * bordo E evento de trilha — o legado só gravava a execução.
 */
export const reassignActionSchema = z.object({
    newAssigneeUserId: uuidSchema,
    reason: z.string().trim().min(1, 'Informe o motivo da redesignação.').max(2000),
});
// ─────────────────────────────────────────────────────────────────────────────
// Circular — sgq.md §D
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Criação da circular.
 *
 * PARIDADE sgq.md §D.1: os limites de tamanho são os do `validateForm` do
 * legado (`controller/circular/circular.php:563-568`), inclusive o mínimo de 10
 * caracteres no título, e a regra "havendo item relacionado, ação é obrigatória"
 * (`circular.php:570-573`).
 *
 * `items` ganha um DISCRIMINADOR (`kind`) que o legado não tinha: produtos e
 * itens livres iam para a MESMA coluna `cod_item` sem nada dizendo qual era qual
 * (BUG-CIR-08), e depois era impossível reconstituir a origem.
 */
export const createCircularSchema = z
    .object({
    title: z
        .string()
        .trim()
        .min(10, 'O título deve ter entre 10 e 255 caracteres.')
        .max(255, 'O título deve ter entre 10 e 255 caracteres.'),
    body: z
        .string()
        .trim()
        .min(10, 'A descrição deve ter entre 10 e 3000 caracteres.')
        .max(3000, 'A descrição deve ter entre 10 e 3000 caracteres.'),
    /** Destinatários explícitos. */
    recipientUserIds: z.array(uuidSchema).max(2000).default([]),
    /** Departamentos inteiros — o "Todos" do legado (`dp_<DEP>_0`). */
    recipientDepartmentIds: z.array(uuidSchema).max(200).default([]),
    /** `true` = toda a organização. Legado: `dp_0_0` → `getUsersStore`. */
    recipientAllOrganization: z.boolean().default(false),
    /** Ações a tomar sobre os itens citados. Legado: `erp_circular_acao`. */
    actionIds: z.array(uuidSchema).max(50).default([]),
    items: z
        .array(z.object({
        /** O discriminador que faltava (BUG-CIR-08). */
        kind: z.enum(['PRODUCT', 'MATERIAL', 'FREE_TEXT']),
        code: z.string().trim().min(1).max(120),
        description: z.string().trim().max(500).default(''),
    }))
        .max(500)
        .default([]),
    /** Prazo para a ciência. Alimenta o job de circular sem ciência. */
    acknowledgementDueDays: z.coerce.number().int().min(1).max(365).default(15),
})
    .superRefine((data, ctx) => {
    if (data.items.length > 0 && data.actionIds.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['actionIds'],
            message: 'Selecione ao menos uma ação quando houver produtos/itens relacionados.',
        });
    }
    if (!data.recipientAllOrganization &&
        data.recipientUserIds.length === 0 &&
        data.recipientDepartmentIds.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['recipientUserIds'],
            message: 'Informe ao menos um destinatário.',
        });
    }
});
/**
 * Edição do RASCUNHO — BUG-CIR-07.
 *
 * O legado não tinha `editCircular` nem `deleteCircular`: um erro de digitação
 * no título era permanente, porque a circular nascia publicada e o e-mail saía
 * dentro do `add` (`controller/circular/circular.php`). O produto novo criou o
 * estado `DRAFT` com exatamente essa justificativa — "corrigir antes de virar
 * evidência" — e não entregava saída nenhuma além de publicar: o rascunho era
 * tão imutável quanto a publicação, e o estado que o usuário alcançava não
 * tinha porta de saída.
 *
 * Só o RASCUNHO é editável. Publicada, a circular é evidência de comunicação
 * com data e destinatários: alterar o texto depois faria a ciência já
 * registrada se referir a um conteúdo que não é o que a pessoa leu (§11.10(e)).
 *
 * Campo ausente significa "não mexi nisso" — mesma guarda anti-wipe de
 * `updateMeetingSchema` (BUG-REU-07): mandar a lista de destinatários vazia
 * porque o formulário não a reenviou não pode esvaziá-la.
 */
export const updateCircularSchema = z
    .object({
    title: z
        .string()
        .trim()
        .min(10, 'O título deve ter entre 10 e 255 caracteres.')
        .max(255, 'O título deve ter entre 10 e 255 caracteres.')
        .optional(),
    body: z
        .string()
        .trim()
        .min(10, 'A descrição deve ter entre 10 e 3000 caracteres.')
        .max(3000, 'A descrição deve ter entre 10 e 3000 caracteres.')
        .optional(),
    recipientUserIds: z.array(uuidSchema).max(2000).optional(),
    recipientDepartmentIds: z.array(uuidSchema).max(200).optional(),
    recipientAllOrganization: z.boolean().optional(),
    acknowledgementDueDays: z.coerce.number().int().min(1).max(365).optional(),
})
    .superRefine((data, ctx) => {
    const touchesRecipients = data.recipientUserIds !== undefined ||
        data.recipientDepartmentIds !== undefined ||
        data.recipientAllOrganization !== undefined;
    if (!touchesRecipients)
        return;
    if (data.recipientAllOrganization !== true &&
        (data.recipientUserIds?.length ?? 0) === 0 &&
        (data.recipientDepartmentIds?.length ?? 0) === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['recipientUserIds'],
            message: 'Informe ao menos um destinatário.',
        });
    }
});
/**
 * Publicação.
 *
 * Estado que o legado não tinha (§D.4): lá a circular nascia publicada e o
 * e-mail saía dentro do `add`. Separar permite revisar antes de a circular virar
 * evidência — e é a partir da publicação que o texto fica imutável.
 */
export const publishCircularSchema = z.object({
    /** Destinatários acrescentados na publicação, além dos já cadastrados. */
    additionalRecipientUserIds: z.array(uuidSchema).max(2000).default([]),
    note: z.string().trim().max(2000).optional(),
});
/**
 * Ciência da circular — ATO ASSINADO (`CIRCULAR_ACKNOWLEDGE`).
 *
 * DIVERGÊNCIA D-CIRC-01 (sgq.md §D.3): no legado NÃO HAVIA botão "li e
 * concordo". A leitura era registrada no simples ato de ABRIR a circular
 * (`controller/circular/circular.php:270`), e `setRead` fazia
 * `UPDATE ... SET lido_em = NOW()` SEM `WHERE lido_em IS NULL` — cada reabertura
 * sobrescrevia o carimbo, de modo que o sistema guardava a ÚLTIMA leitura
 * (BUG-CIR-01). Para evidência de "quando o colaborador tomou ciência", o valor
 * correto é o PRIMEIRO acesso, e a ciência precisa ser ato deliberado.
 *
 * `declaredHostname` preserva o `hostname` do legado — que vinha de
 * `gethostbyaddr` do IP do cliente — mas explicitamente marcado como DECLARADO:
 * IP e user-agent efetivos são observados pelo servidor.
 */
export const acknowledgeCircularSchema = signedCommandSchema.extend({
    /** Declarado pelo cliente, NÃO verificado. Legado: DNS reverso do REMOTE_ADDR. */
    declaredHostname: z.string().trim().max(255).nullish(),
    /** Confirmações de leitura por ação, quando a circular tem `circular_action`. */
    acknowledgedActionIds: z.array(uuidSchema).max(50).default([]),
});
/**
 * Filtros da lista de circulares — sgq.md §D.5.
 *
 * A whitelist de ordenação (`c.id`, `c.titulo`, `c.dt_circular`) é preservada:
 * o legado a tinha e estava certo. O `filter_description` que quebrava a busca
 * em palavras e fazia `OR` de `LIKE` vira `search` simples.
 */
export const circularListFilterSchema = paginationSchema.extend({
    status: arrayQuerySchema(z.enum(enumValues(CircularStatus))).optional(),
    /** `AUTHORED` = emiti; `RECEIVED` = sou destinatário. Legado: `hasPermission`. */
    scope: z.enum(['AUTHORED', 'RECEIVED', 'ALL']).default('ALL'),
    search: z.string().trim().max(255).optional(),
    publishedFrom: z.coerce.date().optional(),
    publishedTo: z.coerce.date().optional(),
    onlyPendingMyAcknowledgement: booleanQuerySchema.default(false),
    sortBy: z.enum(['number', 'title', 'publishedAt']).default('number'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
// ─────────────────────────────────────────────────────────────────────────────
// Indicadores — sgq.md §E
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Consulta dos indicadores de SGQ.
 *
 * É o insumo da ANÁLISE CRÍTICA PELA DIREÇÃO (ISO 13485 §5.6), que exige
 * retroalimentação, reclamação, relato às autoridades, auditoria, monitoramento
 * de processo e ação corretiva/preventiva como entradas.
 *
 * §E.6 do legado é explícito: NÃO EXISTIA tempo médio de tratamento, taxa de
 * reincidência, aderência de prazo, eficácia acumulada, Pareto nem distribuição
 * por categoria Ishikawa. O painel que existia zerava dois dos quatro baldes de
 * NC (`atrasadas` e `em_espera` literais, BUG-IND-03) e, no de tarefas, os
 * baldes SE SOBREPUNHAM e o `total` era a soma deles — maior que o número real
 * de tarefas (BUG-IND-01).
 */
export const sgqIndicatorsQuerySchema = z.object({
    /** Início do período, inclusive. Default: 12 meses atrás. */
    from: z.coerce.date().optional(),
    /** Fim do período, inclusive. Default: hoje. */
    to: z.coerce.date().optional(),
    departmentId: uuidSchema.optional(),
    source: arrayQuerySchema(z.enum(enumValues(NonconformitySource))).optional(),
    type: arrayQuerySchema(z.enum(enumValues(NonconformityType))).optional(),
    /** Granularidade da série temporal. */
    bucket: z.enum(['MONTH', 'QUARTER']).default('MONTH'),
    /** Janela de reincidência, em dias. Default 365. */
    recurrenceWindowDays: z.coerce.number().int().min(30).max(1825).default(365),
});
//# sourceMappingURL=sgq-schemas.js.map